import {
  SpinningLoader,
  ToggleSwitch,
} from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useState } from "react";
import EditPencilIcon from "../../../../../svg/editPencil";
import { formatDateToUTCWithFormat } from "../../../../../utils/formatDate";

const ACTIVE_BORDER_COLOR = "#005D49";
const ACTIVE_BACKGROUND_COLOR = "#E6F3F0";
const INACTIVE_BORDER_COLOR = "#f5f5f5";
const INACTIVE_BACKGROUND_COLOR = "white";
const UPDATED_ON_LABEL = "Updated On";
const UPDATED_ON_FORMAT = "DD MMM YYYY";

/**
 * Formats a GI config timestamp for the card's Updated On field.
 *
 * @param isoDate - ISO timestamp, or empty when unknown
 * @returns Date in `DD MMM YYYY`, or an empty string
 */
const formatUpdatedOn = (isoDate: string): string =>
  formatDateToUTCWithFormat(isoDate, UPDATED_ON_FORMAT);

export interface ViewCardToggleChange {
  name: string;
  required: boolean;
  value: boolean;
}

export interface ViewCardProps {
  title: string;
  id: string;
  items: { label: string; value: string }[];
  onEdit: () => void;
  isActive?: boolean;
  /** ISO timestamp used for the Updated On field. */
  lastUpdated?: string;
  onToggleSwitchChange?: (
    data: ViewCardToggleChange,
  ) => void | Promise<boolean | void>;
}

/**
 * GI configuration card with an active/inactive switch.
 *
 * While a status update is in flight, shows a small spinner and ignores further
 * toggles. If the update fails, remounts the switch and restores inactive/active
 * border and background colors.
 *
 * @param props - Card copy, active state, and toggle/edit handlers
 * @returns The configuration card
 */
const viewCard = ({
  title,
  id,
  items,
  onEdit,
  isActive,
  lastUpdated,
  onToggleSwitchChange,
}: ViewCardProps) => {
  const [checked, setChecked] = useState(Boolean(isActive));
  const [toggleKey, setToggleKey] = useState(0);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updatedOn, setUpdatedOn] = useState(
    formatUpdatedOn(lastUpdated ?? ""),
  );

  useEffect(() => {
    setChecked(Boolean(isActive));
    setUpdatedOn(formatUpdatedOn(lastUpdated ?? ""));
    setToggleKey((previous) => previous + 1);
  }, [isActive, lastUpdated]);

  /**
   * Applies the toggle immediately, then reverts it when the persist call fails.
   *
   * @param nextChecked - Value emitted by the switch
   */
  const handleToggleChange = async (nextChecked: boolean) => {
    if (isUpdating || nextChecked === checked) {
      return;
    }
    const previousChecked = checked;
    setChecked(nextChecked);
    setIsUpdating(true);
    const succeeded = await onToggleSwitchChange?.({
      name: `toggle-switch-${title}`,
      required: true,
      value: nextChecked,
    });
    if (succeeded === false) {
      setChecked(previousChecked);
      setToggleKey((previous) => previous + 1);
    } else if (succeeded) {
      setUpdatedOn(formatUpdatedOn(new Date().toISOString()));
    }
    setIsUpdating(false);
  };

  const borderColor = checked ? ACTIVE_BORDER_COLOR : INACTIVE_BORDER_COLOR;
  const backgroundColor = checked
    ? ACTIVE_BACKGROUND_COLOR
    : INACTIVE_BACKGROUND_COLOR;

  return (
    <div
      className={`sample-card flex max-h-fit w-[49%] flex-col gap-2 rounded-md border p-4 shadow-lg`}
      style={{ borderColor: borderColor, backgroundColor: backgroundColor }}
    >
      <div className="sample-card__header-container flex flex-row items-center justify-between">
        <div className="sample-card__header-container__title flex items-center gap-2 text-h11 font-bold">
          {title}
          <div
            className={`mt-[5px] ${isUpdating ? "pointer-events-none opacity-60" : ""}`}
          >
            <ToggleSwitch
              key={`${id}-${toggleKey}`}
              id={id}
              defaultChecked={checked}
              onChangeEmit={(data) => {
                void handleToggleChange(data.value);
              }}
            />
          </div>
          {isUpdating && (
            <div className="flex h-5 w-5 items-center justify-center">
              <SpinningLoader />
            </div>
          )}
        </div>
        <div
          className="sample-card__header-container__action cursor-pointer"
          onClick={onEdit}
        >
          <EditPencilIcon
            stroke="#0056D6"
            height={16}
            width={16}
            onClick={onEdit}
          />
        </div>
      </div>
      <div className="sample-card__content-container flex w-full justify-between">
        {items.map((item, index) => (
          <div
            className={`sample-card__content-container__item w-1/2 truncate ${(index + 1) % items.length === 0 ? "text-right" : ""}`}
            key={index}
          >
            <span className="text-sm text-primaryGray-4">{item.label} : </span>
            <span className="text-h12 font-semibold">
              {item.label === UPDATED_ON_LABEL ? updatedOn : item.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default viewCard;
